import React from 'react';
import Layout from '@/common/Layout';
import {
  Row,
  Col,
  Form,
  Card,
  Input,
  Select,
  Space,
  message,
  Image
} from 'antd';
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomButton from '@/common/CustomButton';
import PasswordConfirmModal from '@/common/PasswordConfirmModal';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getUserById,
  UserCreate,
  updateUser,
  getAllDesignations,
  getAllDepartments
} from '../../redux/slice';
import { users as fetchUsersList } from '../../redux/api';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { history } from 'app/history';
import User from '../../images/user.gif';

const createUser = () => {
  const {
    allDepartments,
    allDisignations,
    user: loadedUser
  } = useSelector((state) => state.admin);

  const [form] = Form.useForm();
  const searchParam = useLocation().search;
  const searchParams = new URLSearchParams(searchParam);

  const [account, setAccount] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [dept, setDept] = useState('');
  const [subDept, setSubDept] = useState('');
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdModalLoading, setPwdModalLoading] = useState(false);
  const [pwdModalError, setPwdModalError] = useState('');
  const [pendingValues, setPendingValues] = useState(null);
  const empIdCheckTimer = useRef(null);
  const dispatch = useDispatch();

  // Case-insensitive substring filter — used by every searchable Select on this form.
  const dropdownFilterOption = (input, option) => {
    const label = (option?.children ?? '').toString().toLowerCase();
    return label.includes((input || '').toLowerCase());
  };

  // Debounced async duplicate check for employee_id (create mode only). Reads from the
  // existing user_list endpoint, then filters for an exact (case-insensitive) match so
  // a partial iLike result like "EMP1234" doesn't fire on "EMP123".
  const validateEmployeeIdUnique = (_, value) => {
    if (searchParams.get('id')) return Promise.resolve(); // edit mode — skip
    const trimmed = (value || '').trim();
    if (!trimmed) return Promise.resolve(); // `required` handles empty

    return new Promise((resolve, reject) => {
      if (empIdCheckTimer.current) clearTimeout(empIdCheckTimer.current);
      empIdCheckTimer.current = setTimeout(async () => {
        try {
          const resp = await fetchUsersList('user-list', {
            search: trimmed,
            page: 1,
            per_page: 20
          });
          const match = (resp?.data || []).find(
            (u) => (u.employee_id || '').toLowerCase() === trimmed.toLowerCase()
          );
          if (match) {
            if (match.is_active === false) {
              return reject(
                new Error(
                  'Employee ID belongs to a deactivated user. Reactivate from the users list instead.'
                )
              );
            }
            return reject(new Error('Employee ID is already in use.'));
          }
          resolve();
        } catch (e) {
          resolve(); // network blip — let the backend make the final call on submit
        }
      }, 500);
    });
  };
  useEffect(() => {
    handleGetUserById();
  }, []);

  useEffect(() => {
    handleGetAllDepartments();
    handleGetAllDesignations();
  }, []);

  const handleGetAllDepartments = () => {
    const payload = {};
    dispatch(getAllDepartments(payload)).then((response) => {
      setSubDept(response?.payload?.data.filter((item) => item.id !== 12));
    });
  };

  const handleGetAllDesignations = () => {
    const payload = {};
    dispatch(getAllDesignations(payload));
  };

  const handleGetUserById = () => {
    const payload = { user_id: searchParams.get('id') };
    dispatch(getUserById(payload)).then((response) => {
      form.setFieldsValue({
        first_name: response?.payload?.data?.first_name,
        last_name: response?.payload?.data?.last_name,
        middle_name: response?.payload?.data?.middle_name,
        role: response?.payload?.data?.department
          ? `${response?.payload?.data?.department}`
          : '',
        email: response?.payload?.data?.email,
        permission: response?.payload?.data?.level
          ? `${response?.payload?.data?.level}`
          : '',
        department: response?.payload?.data?.department_id
          ? response?.payload?.data?.department_id
          : '',
        emp_id: response?.payload?.data?.employee_id,
        user_id: response?.payload?.data?.employee_id,
        designation_id: response?.payload?.data?.designation_id
          ? response?.payload?.data?.designation_id
          : ''
      });
    });
  };

  const norm = (v) => (v == null ? '' : String(v));
  const hasUserDataChanged = (values, original) => {
    if (!original || !original.id) return true;
    const deptOrig =
      original.department_id == null ? '' : String(original.department_id);
    const desgOrig =
      original.designation_id == null ? '' : String(original.designation_id);
    const levelOrig = original.level == null ? '' : String(original.level);
    return (
      norm(values.first_name) !== norm(original.first_name) ||
      norm(values.last_name) !== norm(original.last_name) ||
      norm(values.middle_name) !== norm(original.middle_name) ||
      norm(values.email) !== norm(original.email) ||
      norm(values.emp_id) !== norm(original.employee_id) ||
      norm(values.department) !== deptOrig ||
      norm(values.designation_id) !== desgOrig ||
      norm(values.permission) !== levelOrig
    );
  };

  const buildUpdatePayload = (values, password) => ({
    first_name: values?.first_name,
    last_name: values?.last_name,
    middle_name: values?.middle_name,
    email: values?.email,
    role: values?.department,
    department: values?.department,
    level: values?.permission,
    employee_id: values?.emp_id,
    designation_id: values?.designation_id,
    user_id: searchParams.get('id'),
    qa_dept: rolePermissions ? rolePermissions : [],
    password
  });

  const submitUpdate = (values, password) => {
    setPwdModalLoading(true);
    setPwdModalError('');
    dispatch(updateUser(buildUpdatePayload(values, password))).then(
      (response) => {
        const payload = response?.payload || {};
        if (payload.success) {
          setPwdModalLoading(false);
          setPwdModalVisible(false);
          setPendingValues(null);
          message.success(payload.message || 'Updated successfully');
          history.push('users-list');
        } else {
          setPwdModalLoading(false);
          const msg =
            payload.message || 'Unable to save changes. Please try again.';
          // 403 = wrong password, 400 = missing — keep modal open and show inline error
          setPwdModalError(msg);
        }
      }
    );
  };

  const onFinish = (values) => {
    if (searchParams.get('id')) {
      // Edit mode — gate save behind password confirmation
      if (!hasUserDataChanged(values, loadedUser)) {
        message.info('No changes to save.');
        return;
      }
      setPendingValues(values);
      setPwdModalError('');
      setPwdModalVisible(true);
      return;
    }

    // Create mode — block re-entry while a previous submit is still in-flight
    if (account) return;
    setAccount(true);
    const payload = {
      first_name: values?.first_name?.trim(),
      last_name: values?.last_name?.trim(),
      middle_name: values?.middle_name?.trim(),
      email: values?.email?.trim(),
      role: values?.department,
      department: values?.department,
      level: values?.permission,
      employee_id: values?.emp_id?.trim(),
      designation_id: values?.designation_id,
      user_id: searchParams.get('id'),
      qa_dept: rolePermissions ? rolePermissions : []
    };
    dispatch(UserCreate(payload)).then((response) => {
      if (response?.payload?.success) {
        message.success(response?.payload?.message);
        setAccount(false);
        history.push('users-list');
      } else {
        message.error(response?.payload?.message);
        setAccount(false);
      }
    });
  };

  const handlePwdConfirm = (password) => {
    if (!pendingValues) return;
    submitUpdate(pendingValues, password);
  };

  const handlePwdCancel = () => {
    if (pwdModalLoading) return;
    setPwdModalVisible(false);
    setPwdModalError('');
    setPendingValues(null);
  };
  const handleAddmore = () => {
    const list = [...rolePermissions];
    list.push({ role: '', permission: '' });
    setRolePermissions(list);
  };

  const handleChangeDept = (e) => {
    setDept(e);
    if (e == 12) {
      const list = [...rolePermissions];
      list.push({ role: '', permission: '' });
      setRolePermissions(list);
    }
  };

  const handleChangeperm = (e, index, type) => {
    const list = [...rolePermissions];

    type === 'department'
      ? (list[index].role = e)
      : (list[index].permission = e);
    setRolePermissions(list);

    let result = subDept.filter(
      (o1) => !rolePermissions.some((o2) => o1.id === o2.role)
    );
    setSubDept(result);
  };

  return (
    <Layout sider={false}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card
          title={
            searchParams.get('id') && isEdit
              ? 'UPDATE USER'
              : searchParams.get('id') && !isEdit
              ? 'VIEW DETAILS'
              : 'NEW USER'
          }
          className={styles.CustomPanel}
          extra={
            searchParams.get('id') ? (
              <CustomButton onClick={() => setIsEdit(true)}>
                <EditOutlined />
              </CustomButton>
            ) : (
              ''
            )
          }
        >
          <Row gutter={24}>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="First Name"
                name="first_name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter first name'
                  }
                ]}
                className={styles.customLable}
              >
                <Input
                  placeholder="Enter first name"
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="Middle Name"
                name="middle_name"
                rules={[
                  {
                    required: false,
                    message: 'Enter middle name'
                  }
                ]}
                className={styles.customLable}
              >
                <Input
                  placeholder="Enter middle name"
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="Last Name"
                name="last_name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter last name'
                  }
                ]}
                className={styles.customLable}
              >
                <Input
                  placeholder="Enter last name"
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="Employee ID"
                name="emp_id"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter employee ID'
                  },
                  { validator: validateEmployeeIdUnique }
                ]}
                validateTrigger={['onBlur', 'onChange']}
                className={styles.customLable}
              >
                <Input
                  placeholder="Enter employee ID"
                  onChange={(e) =>
                    form.setFieldsValue({ user_id: e.target.value })
                  }
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="User ID"
                name="user_id"
                rules={[
                  {
                    required: false,
                    message: 'Enter user ID'
                  }
                ]}
                className={styles.customLable}
              >
                <Input placeholder="Enter user ID" disabled={true} />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="E-mail"
                name="email"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter e-mail'
                  }
                ]}
                className={styles.customLable}
              >
                <Input
                  placeholder="Enter e-mail"
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="Department"
                name="department"
                rules={[
                  {
                    required: true,
                    message: 'Enter department'
                  }
                ]}
                className={styles.customLable}
              >
                <Select
                  placeholder="Select Department"
                  showSearch
                  optionFilterProp="children"
                  filterOption={dropdownFilterOption}
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                  onChange={(e) => handleChangeDept(e)}
                >
                  {allDepartments?.map((item, index) => {
                    return (
                      <option value={item?.id} key={index}>
                        {item?.department}
                      </option>
                    );
                  })}
                </Select>
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="Designation"
                name="designation_id"
                rules={[
                  {
                    required: true,
                    message: 'Enter Designation'
                  }
                ]}
                className={styles.customLable}
                placeholder="Select Designation"
              >
                <Select
                  placeholder="Select Designation"
                  showSearch
                  optionFilterProp="children"
                  filterOption={dropdownFilterOption}
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                >
                  {allDisignations?.map((item, index) => {
                    return (
                      <option value={item?.id} key={index}>
                        {item?.designation}
                      </option>
                    );
                  })}
                </Select>
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8} md={24} sm={24}>
              <FormItem
                label="Permission"
                name="permission"
                rules={[
                  {
                    required: true,
                    message: 'Select Permission'
                  }
                ]}
                className={styles.customLable}
              >
                <Select
                  placeholder="Select Permissions"
                  showSearch
                  optionFilterProp="children"
                  filterOption={dropdownFilterOption}
                  disabled={searchParams.get('id') && !isEdit ? true : false}
                >
                  <option value="1">Level-1</option>
                  <option value="2">Level-2</option>
                  <option value="3">Level-3</option>
                </Select>
              </FormItem>
            </Col>
          </Row>
        </Card>
        {dept == 12 ? (
          <Card className={styles.CustomPanelFG1}>
            {rolePermissions?.map((item, index) => {
              return (
                <Row gutter={24} key={index}>
                  <Col xs={24} xl={8} span={8} md={24} sm={24}>
                    <FormItem
                      label="Department"
                      rules={[
                        {
                          required: true,
                          message: 'Enter department'
                        }
                      ]}
                      className={styles.customLable}
                    >
                      <Select
                        placeholder="Select Department"
                        showSearch
                        optionFilterProp="children"
                        filterOption={dropdownFilterOption}
                        disabled={
                          searchParams.get('id') && !isEdit ? true : false
                        }
                        onChange={(e) =>
                          handleChangeperm(e, index, 'department')
                        }
                      >
                        {subDept?.map((item, index) => {
                          return (
                            <option value={item?.id} key={index}>
                              {item?.department}
                            </option>
                          );
                        })}
                      </Select>
                    </FormItem>
                  </Col>
                  <Col xs={24} xl={8} span={8} md={24} sm={24}>
                    <FormItem
                      label="Permission"
                      rules={[
                        {
                          required: true,
                          message: 'Select Permission'
                        }
                      ]}
                      className={styles.customLable}
                    >
                      <Select
                        placeholder="Select Permissions"
                        showSearch
                        optionFilterProp="children"
                        filterOption={dropdownFilterOption}
                        disabled={
                          searchParams.get('id') && !isEdit ? true : false
                        }
                        onChange={(e) =>
                          handleChangeperm(e, index, 'permission')
                        }
                      >
                        <option value="1">Level-1</option>
                        <option value="2">Level-2</option>
                        <option value="3">Level-3</option>
                      </Select>
                    </FormItem>
                  </Col>
                  <Col xs={24} xl={8} span={8} md={24} sm={24}>
                    <FormItem
                      label="Add"
                      rules={[
                        {
                          required: true,
                          message: 'Select Permission'
                        }
                      ]}
                      className={styles.customLable}
                    >
                      <CustomButton onClick={() => handleAddmore(index)}>
                        <PlusOutlined />
                      </CustomButton>
                    </FormItem>
                  </Col>
                </Row>
              );
            })}
          </Card>
        ) : (
          ''
        )}
        <br />
        <FormItem className="text-center">
          {searchParams.get('id') && isEdit ? (
            <Space>
              <CustomButton
                className={styles.inwardButton}
                htmlType="submit"
                type="primary"
              >
                Update
              </CustomButton>
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                ghost
                onClick={() => history.back()}
              >
                Back
              </CustomButton>
            </Space>
          ) : searchParams.get('id') && !isEdit ? (
            <CustomButton
              className={styles.inwardButton}
              type="primary"
              ghost
              onClick={() => history.back()}
            >
              Back
            </CustomButton>
          ) : (
            <Space>
              {account ? (
                <Image
                  src={User}
                  preview={false}
                  style={{ width: '100px !important', height: '100px' }}
                />
              ) : (
                <CustomButton
                  className={styles.inwardButton}
                  htmlType="submit"
                  type="primary"
                >
                  Create
                </CustomButton>
              )}
              <CustomButton
                className={styles.inwardButton}
                type="primary"
                ghost
                onClick={() => history.back()}
              >
                Back
              </CustomButton>
            </Space>
          )}
        </FormItem>
      </Form>
      <PasswordConfirmModal
        visible={pwdModalVisible}
        title="Confirm changes with your password"
        description="Please re-enter your password to save these user changes."
        okText="Confirm & Save"
        loading={pwdModalLoading}
        errorMessage={pwdModalError}
        onConfirm={handlePwdConfirm}
        onCancel={handlePwdCancel}
      />
    </Layout>
  );
};
export default createUser;
